// הגדרות: משתמשים והרשאות, יומן פעולות, שימוש AI.
import { useState } from 'react';
import { api } from '../../api';
import { Empty, ErrorBox, Field, Loading, Modal, fmtDate, useLoad, useMe } from '../ui';
import { Check, useAction } from './common';

interface UserRow { id: string; email: string; name: string; role: 'admin' | 'editor' | 'viewer'; can_approve_specs: boolean; can_export_delivery: boolean }
export const ROLES: Record<string, string> = { admin: 'מנהל מערכת', editor: 'עורך', viewer: 'צופה' };

export function UsersAdmin() {
  const me = useMe();
  const { data, error, loading, reload, setData } = useLoad<UserRow[]>('/api/users');
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState<{ name: string; url: string } | null>(null);
  const act = useAction();
  const makeLink = async (u: UserRow) => {
    const r = await act.run(() => api.post(`/api/users/${u.id}/password-link`));
    if (r) setLink({ name: u.name, url: r.url });
  };
  const patch = async (u: UserRow, body: Partial<{ role: string; canApproveSpecs: boolean; canExportDelivery: boolean }>) => {
    if (u.id === me.user.id && body.role && body.role !== 'admin' && !window.confirm('אתם עומדים להסיר מעצמכם הרשאת מנהל. להמשיך?')) return;
    if (await act.run(() => api.patch(`/api/users/${u.id}`, body), `ההרשאות של ${u.name} עודכנו`)) {
      setData((data ?? []).map((x) => (x.id !== u.id ? x : { ...x, role: (body.role as UserRow['role']) ?? x.role, can_approve_specs: body.canApproveSpecs ?? x.can_approve_specs, can_export_delivery: body.canExportDelivery ?? x.can_export_delivery })));
    }
  };
  return (
    <div className="stack">
      <div className="row between">
        <div className="small muted">מנהל: ספקים, API, תקציבים ומשתמשים · עורך: לקוחות ובאנרים · צופה: צפייה בתוצרים בלבד. אישור מפרטים ויצוא למסירה הן הרשאות נפרדות.</div>
        <button className="btn primary" onClick={() => setCreating(true)}>משתמש חדש</button>
      </div>
      <ErrorBox error={error} /><ErrorBox error={act.error} />
      {loading && !data && <Loading />}
      {data && (
        <table className="table cards-sm">
          <thead><tr><th>משתמש</th><th>תפקיד</th><th>אישור מפרטים</th><th>יצוא למסירה</th><th>סיסמה</th></tr></thead>
          <tbody>{data.map((u) => (
            <tr key={u.id}>
              <td><strong>{u.name}</strong>{u.id === me.user.id && <span className="badge info"> אתם</span>}<div className="small ltr">{u.email}</div></td>
              <td data-l="תפקיד">
                <label className="sr-only" htmlFor={`role-${u.id}`}>תפקיד של {u.name}</label>
                <select id={`role-${u.id}`} value={u.role} disabled={act.busy} onChange={(e) => patch(u, { role: e.target.value })}>{Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              </td>
              <td data-l="אישור מפרטים"><Check checked={u.can_approve_specs} disabled={act.busy} onChange={(v) => patch(u, { canApproveSpecs: v })}><span className="sr-only">אישור מפרטים ל{u.name}</span></Check></td>
              <td data-l="יצוא למסירה"><Check checked={u.can_export_delivery} disabled={act.busy} onChange={(v) => patch(u, { canExportDelivery: v })}><span className="sr-only">יצוא למסירה ל{u.name}</span></Check></td>
              <td data-l="סיסמה"><button className="btn small" disabled={act.busy} onClick={() => makeLink(u)}>קישור לקביעת סיסמה</button></td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {creating && <NewUserModal onClose={() => setCreating(false)} onCreated={(r) => { reload(); if (r) setLink(r); }} />}
      {link && <LinkModal {...link} onClose={() => setLink(null)} />}
    </div>
  );
}

function LinkModal({ name, url, onClose }: { name: string; url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title={`קישור לקביעת סיסמה — ${name}`} onClose={onClose}>
      <div className="stack">
        <div className="small">שלחו את הקישור למשתמש. הוא חד-פעמי ותקף 48 שעות; המשתמש בוחר בו סיסמה בעצמו.</div>
        <input readOnly className="ltr mono" value={url} onFocus={(e) => e.currentTarget.select()} aria-label="קישור" />
        <div className="row"><button className="btn primary" onClick={() => { navigator.clipboard?.writeText(url).then(() => setCopied(true)).catch(() => {}); }}>{copied ? 'הועתק' : 'העתקה'}</button><button className="btn" onClick={onClose}>סגירה</button></div>
      </div>
    </Modal>
  );
}

function NewUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: (link: { name: string; url: string } | null) => void }) {
  const [f, setF] = useState({ name: '', email: '', password: '', role: 'editor', canApproveSpecs: false, canExportDelivery: false });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const { busy, error, run } = useAction();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await run(() => api.post('/api/users', { ...f, password: f.password || undefined }), 'המשתמש נוצר');
    if (r) { onCreated(r.setupLink ? { name: f.name, url: r.setupLink } : null); onClose(); }
  };
  return (
    <Modal title="משתמש חדש" onClose={onClose}>
      <form className="stack" onSubmit={submit} autoComplete="off">
        <Field label="שם *"><input required value={f.name} onChange={(e) => set({ name: e.target.value })} /></Field>
        <Field label="אימייל *"><input required type="email" className="ltr" value={f.email} onChange={(e) => set({ email: e.target.value })} autoComplete="off" /></Field>
        <Field label="סיסמה ראשונית (לא חובה)" hint="ריק = המשתמש יקבל קישור לקביעת סיסמה בעצמו (מומלץ)"><input type="password" minLength={10} className="ltr" value={f.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" /></Field>
        <Field label="תפקיד"><select value={f.role} onChange={(e) => set({ role: e.target.value })}>{Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
        <Check checked={f.canApproveSpecs} onChange={(v) => set({ canApproveSpecs: v })}>הרשאה לאשר מפרטי מדיה</Check>
        <Check checked={f.canExportDelivery} onChange={(v) => set({ canExportDelivery: v })}>הרשאה ליצוא מוכן למסירה</Check>
        <ErrorBox error={error} />
        <div className="row"><button className="btn primary" disabled={busy || (f.password.length > 0 && f.password.length < 10)}>{busy ? 'יוצר…' : 'יצירה'}</button><button type="button" className="btn" onClick={onClose}>ביטול</button></div>
      </form>
    </Modal>
  );
}

interface AuditRow { id: string; user_id: string | null; action: string; entity_type: string; entity_id: string | null; data: unknown; created_at: string }
export function AuditLog() {
  const { data, error, loading, reload } = useLoad<AuditRow[]>('/api/audit');
  const users = useLoad<UserRow[]>('/api/users');
  const [q, setQ] = useState('');
  const uname = (id: string | null) => users.data?.find((u) => u.id === id)?.name ?? id ?? 'מערכת';
  const rows = (data ?? []).filter((r) => !q || r.action.includes(q) || r.entity_type.includes(q) || (r.entity_id ?? '').includes(q));
  return (
    <div className="stack">
      <div className="toolbar"><Field label="סינון (פעולה / ישות)"><input type="search" className="ltr" value={q} onChange={(e) => setQ(e.target.value)} placeholder="spec.approve" /></Field><button className="btn" onClick={reload}>רענון</button></div>
      <ErrorBox error={error} />
      {loading && !data && <Loading />}
      {data && rows.length === 0 && <Empty>אין רשומות.</Empty>}
      {rows.length > 0 && (
        <table className="table cards-sm">
          <thead><tr><th>זמן</th><th>משתמש</th><th>פעולה</th><th>ישות</th><th>פרטים</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id}>
              <td className="small">{fmtDate(r.created_at)}</td>
              <td data-l="משתמש">{uname(r.user_id)}</td>
              <td data-l="פעולה" className="mono">{r.action}</td>
              <td data-l="ישות" className="mono small">{r.entity_type}{r.entity_id ? ` ${r.entity_id}` : ''}</td>
              <td data-l="פרטים"><pre className="json-val" style={{ maxWidth: 360, maxHeight: 90, overflow: 'auto' }}>{r.data && JSON.stringify(r.data) !== '{}' ? JSON.stringify(r.data) : ''}</pre></td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

interface UsageRow { id: string; job_id: string | null; banner_set_id: string | null; provider: string; model: string; operation: string; input_tokens: number; output_tokens: number; duration_ms: number; cost_usd: string | number; cost_is_estimate: boolean; success: boolean; created_at: string }
export function UsageLog() {
  const { data, error, loading, reload } = useLoad<UsageRow[]>('/api/ai-usage');
  return (
    <div className="stack">
      <div className="row between"><span className="small muted">200 הקריאות האחרונות. עלות "אומדן" מחושבת לפי מחירון מוגדר כשהספק אינו מחזיר עלות.</span><button className="btn" onClick={reload}>רענון</button></div>
      <ErrorBox error={error} />
      {loading && !data && <Loading />}
      {data && data.length === 0 && <Empty>אין קריאות AI מתועדות.</Empty>}
      {data && data.length > 0 && (
        <table className="table cards-sm">
          <thead><tr><th>זמן</th><th>פעולה</th><th>ספק / מודל</th><th>tokens (קלט/פלט)</th><th>משך</th><th>עלות</th><th>תוצאה</th></tr></thead>
          <tbody>{data.map((u) => (
            <tr key={u.id}>
              <td className="small">{fmtDate(u.created_at)}</td>
              <td data-l="פעולה" className="mono">{u.operation}</td>
              <td data-l="ספק / מודל" className="mono small">{u.provider} · {u.model}</td>
              <td data-l="tokens" className="ltr">{u.input_tokens.toLocaleString()} / {u.output_tokens.toLocaleString()}</td>
              <td data-l="משך" className="ltr">{(u.duration_ms / 1000).toFixed(1)}s</td>
              <td data-l="עלות" className="ltr">${Number(u.cost_usd).toFixed(4)} {u.cost_is_estimate && <span className="badge warn">אומדן</span>}</td>
              <td data-l="תוצאה">{u.success ? <span className="badge ok">הצליח</span> : <span className="badge err">נכשל</span>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
